/**
 * The coach's read-only view of participants' trackers: when coach access
 * ends, the List chips, the weekly table and the text a coach copies before
 * a call. Pure, so the server, the screens and the tests share one rule.
 */

import { addDays, mondayOf, summaryWeekNumber } from './checkin-weeks';
import { participantChannels, TrackerConfig } from './tracker-config';
import { funnelsLine, leadsSetUpLine } from './tracker-leads';
import {
  cplOf,
  rollupRange,
  rollupWeek,
  TrackerCloseAs,
  TrackerRollup,
  TrackerTouchInput,
} from './tracker-rollup';
import {
  fmtMoney,
  fmtShortDate,
  nextText,
  personName,
  NO_BET_ID,
  personOfferText,
  sortGroup,
  touchesOfGroup,
  trackerBetGroups,
  TrackerBetGroup,
  stageLabel,
  touchesByPerson,
  trackerCountsLine,
  trackerGroup,
  TrackerGroup,
  TrackerNextText,
  TrackerPerson,
  TRACKER_GROUP_ORDER,
  TRACKER_GROUP_TITLES,
} from './tracker-people';
import {
  betRollupLines,
  betsListLine,
  betsScoreboardLine,
  scoreboardLine,
  trackerSpend,
  TrackerSummaryData,
} from './tracker-summary';

/** Days of coach access after the standup end date. */
export const COACH_ACCESS_GRACE_DAYS = 1;

/** The last day a coach can read individual trackers: the day after the standup end date. */
export function coachAccessLastDay(endDate: string) {
  return addDays(endDate, COACH_ACCESS_GRACE_DAYS);
}

/** Coach access to individual trackers has ended. With no end date it never ends. */
export function coachAccessEnded(endDate: string | undefined, today: string) {
  return !!endDate && today > coachAccessLastDay(endDate);
}

/** One participant's counts this week, with every dated touch counted in `touches`. */
export type CoachTrackerCounts = TrackerRollup & { touches: number };

/** One bet's figures for the early-warning chips. */
export type CoachTrackerBet = {
  name: string;
  /** Dated touches of the bet's people this week. */
  touches: number;
  /**
   * Conversations the bet's people booked in the last 14 days, today
   * included; on a Leads course, the people who reached the destination.
   */
  booked14: number;
  /** The day of the bet's first step touch, YYYY-MM-DD; on Leads, outside lagging channels. */
  firstStepOn?: string;
  /** Leads: this week's touches outside lagging channels, which the leads rule reads. */
  steadyTouches?: number;
  /** The "No bet" group: never "No touches this week", as finished pre-bets people stay quiet. */
  noBet?: boolean;
};

export type CoachTrackerStudent = {
  userId: string;
  now: CoachTrackerCounts;
  before: TrackerRollup;
  /**
   * Conversations booked in the last 14 days, today included; on a Leads
   * course, new leads in those days.
   */
  booked14: number;
  /** A Leads course: the chips read leads, not bookings. */
  countsLeads?: boolean;
  /** Leads: the participant's own cost-per-lead target. */
  cplTargetCents?: number;
  closeAs?: TrackerCloseAs;
  /** The participant's bets in order, on a course with bets on; empty until they add one. */
  bets: CoachTrackerBet[];
  /** The day of the participant's first step touch, YYYY-MM-DD; on Leads, outside lagging channels. */
  firstStepOn?: string;
  /**
   * Leads: this week's touches outside lagging channels (laggingStageKeys).
   * The leads rule reads these, so a week spent only pitching podcasts is
   * not a stall. Absent: `now.touches`.
   */
  steadyTouches?: number;
  /** The participant's own today. */
  today: string;
};

/**
 * Outreach that began at least 14 days ago, today included: only then can
 * "nothing booked in 14 days" mean something. Two sends on day one are not
 * a warning.
 */
export function outreach14DaysOld(firstStepOn: string | undefined, today: string) {
  return !!firstStepOn && firstStepOn <= addDays(today, -13);
}

/**
 * What the chips read from a set of touches: this week's, bookings in 14
 * days, the first step. Steps on `lagging` stages (laggingStageKeys) still
 * count as touches and their leads still count, but they neither start the
 * 14 days nor make a week "worked" for the leads rule.
 */
export function coachChipFigures(
  config: TrackerConfig,
  touches: TrackerTouchInput[],
  closeAs: TrackerCloseAs | undefined,
  today: string,
  lagging?: Set<string>,
) {
  const thisWeek = mondayOf(today);
  const weekEnd = addDays(thisWeek, 6);
  const inWeek = (t: TrackerTouchInput) => t.happenedOn >= thisWeek && t.happenedOn <= weekEnd;
  const steady = lagging?.size
    ? touches.filter((t) => !t.stageKey || !lagging.has(t.stageKey))
    : touches;
  const last14 = rollupRange(config, touches, closeAs, addDays(today, -13), today);
  return {
    touches: touches.filter(inWeek).length,
    steadyTouches: steady.filter(inWeek).length,
    booked14: participantChannels(config) ? last14.leads : last14.booked,
    firstStepOn: steady
      .filter((t) => t.kind === 'step')
      .map((t) => t.happenedOn)
      .sort()[0],
  };
}

/**
 * Each group's chip figures (trackerBetGroups): the bets in order, and "No
 * bet" when some people have none, so the booking rule still sees them.
 * Empty with no bets: the chips then run on the whole tracker.
 */
export function coachTrackerBets(opts: {
  config: TrackerConfig;
  bets: TrackerBetGroup[];
  people: Array<{ id: string; betId?: string }>;
  touches: TrackerTouchInput[];
  closeAs: TrackerCloseAs | undefined;
  today: string;
  /** Leads: the participant's lagging stage keys (laggingStageKeys). */
  lagging?: Set<string>;
}): CoachTrackerBet[] {
  const { config, bets, people, touches, closeAs, today, lagging } = opts;
  return trackerBetGroups(config, bets, people).map((group) => ({
    name: group.name,
    ...coachChipFigures(
      config,
      touchesOfGroup(bets, people, touches, group.id),
      closeAs,
      today,
      lagging,
    ),
    ...(group.id === NO_BET_ID ? { noBet: true } : {}),
  }));
}

/** No dated touch of any kind this week (Monday to Sunday). */
export function noTouchesThisWeek(student: Pick<CoachTrackerStudent, 'now'>) {
  return student.now.touches === 0;
}

/**
 * Logging this week, outreach under way for 14 days or more, and no
 * conversation booked in the last 14 days.
 */
export function nothingBooked14(
  student: Pick<
    CoachTrackerStudent,
    'now' | 'booked14' | 'firstStepOn' | 'today' | 'steadyTouches'
  >,
) {
  return (
    (student.steadyTouches ?? student.now.touches) > 0 &&
    student.booked14 === 0 &&
    outreach14DaysOld(student.firstStepOn, student.today)
  );
}

export type TrackerChip = { label: string; title: string };

/**
 * The List's early-warning chips, at most two: one per rule. With bets the
 * rules run per bet and each chip lists its bets: "No touches this week:
 * Consultants, Speakers" for bets with nothing logged while the participant
 * logged for others, and "Nothing booked in 14 days: Coaches" for bets worked
 * this week, first worked 14 or more days ago, with no booking in 14 days.
 * On Leads, "worked" and "first worked" leave out lagging channels
 * (laggingStageKeys), so pitching podcasts alone never reads as a stall.
 * A participant with no touches at all
 * keeps the one "No touches this week"; with no bets the rules run on the
 * whole tracker.
 */
export function trackerChips(
  student: Pick<
    CoachTrackerStudent,
    'now' | 'booked14' | 'bets' | 'firstStepOn' | 'today' | 'countsLeads' | 'steadyTouches'
  >,
) {
  const chips: TrackerChip[] = [];
  const quiet = student.countsLeads ? 'No leads in 14 days' : 'Nothing booked in 14 days';
  const none = student.countsLeads
    ? 'no one reached the destination in the last 14 days'
    : 'no conversation booked in the last 14 days';
  if (noTouchesThisWeek(student)) {
    chips.push({
      label: 'No touches this week',
      title: student.countsLeads
        ? 'Nothing logged this week: no steps on any channel.'
        : 'Nothing logged this week: no sends, replies, conversations, closes or payments.',
    });
    return chips;
  }
  if (!student.bets.length) {
    if (nothingBooked14(student)) {
      chips.push({
        label: quiet,
        title: `Logging, outreach started 14 or more days ago, and ${none}.`,
      });
    }
    return chips;
  }
  const idle = student.bets.filter((b) => !b.noBet && b.touches === 0).map((b) => b.name);
  const unbooked = student.bets
    .filter(
      (b) =>
        (b.steadyTouches ?? b.touches) > 0 &&
        b.booked14 === 0 &&
        outreach14DaysOld(b.firstStepOn, student.today),
    )
    .map((b) => b.name);
  if (idle.length) {
    const label = `No touches this week: ${idle.join(', ')}`;
    chips.push({ label, title: `${label}. Logging this week, but not for these bets.` });
  }
  if (unbooked.length) {
    const label = `${quiet}: ${unbooked.join(', ')}`;
    chips.push({
      label,
      title: `${label}. Worked this week, first worked 14 or more days ago, and ${none}.`,
    });
  }
  return chips;
}

/** "Nov 16–22", or "Nov 30–Dec 6" across a month. */
export function fmtWeekRange(weekStart: string) {
  const start = mondayOf(weekStart);
  const end = addDays(start, 6);
  if (start.slice(0, 7) === end.slice(0, 7)) {
    return `${fmtShortDate(start)}–${Number(end.slice(8, 10))}`;
  }
  return `${fmtShortDate(start)}–${fmtShortDate(end)}`;
}

/** "Week 3", or "Before week 1" for touches logged before standups started. */
export function weekLabel(weekNumber: number) {
  return weekNumber >= 1 ? `Week ${weekNumber}` : 'Before week 1';
}

/**
 * The weeks a table covers: from the earlier of the standup start's week and
 * the first touch's week, to this week, or to the week holding the last day
 * of coach access once that has passed. Weeks are numbered from the standup
 * start, as standups number them; earlier weeks are "Before week 1".
 */
export function trackerWeeks(opts: {
  startDate?: string;
  endDate?: string;
  firstTouchOn?: string;
  today: string;
}) {
  const thisWeek = mondayOf(opts.today);
  const endWeek = opts.endDate ? mondayOf(coachAccessLastDay(opts.endDate)) : undefined;
  const lastWeek = endWeek && endWeek < thisWeek ? endWeek : thisWeek;
  const startWeek = opts.startDate ? mondayOf(opts.startDate) : undefined;
  const touchWeek = opts.firstTouchOn ? mondayOf(opts.firstTouchOn) : undefined;
  let first = [startWeek, touchWeek].filter((w): w is string => !!w).sort()[0] ?? lastWeek;
  if (first > lastWeek) {
    first = lastWeek;
  }
  const numberFrom = startWeek ?? first;
  const weeks: Array<{ weekStart: string; weekNumber: number; isCurrent: boolean }> = [];
  for (let week = first; week <= lastWeek; week = addDays(week, 7)) {
    weeks.push({
      weekStart: week,
      weekNumber: summaryWeekNumber(week, numberFrom) ?? 1,
      isCurrent: week === thisWeek,
    });
  }
  return weeks;
}

export type CoachTrackerData = TrackerSummaryData & { startDate?: string; endDate?: string };

export type TrackerWeekRow = {
  weekStart: string;
  weekNumber: number;
  isCurrent: boolean;
  rollup: TrackerRollup;
};

/**
 * One participant's weeks, oldest first, and their total. With `betId` the
 * figures are that bet's, over the same weeks as the whole tracker.
 */
export function participantWeekRows(data: CoachTrackerData, betId?: string) {
  const closeAs = data.participant.closeAs;
  const dates = data.touches.map((t) => t.happenedOn).sort();
  const weeks = trackerWeeks({
    startDate: data.startDate,
    endDate: data.endDate,
    firstTouchOn: dates[0],
    today: data.today,
  });
  const touches = betId
    ? touchesOfGroup(data.bets, data.people, data.touches, betId)
    : data.touches;
  const spend = betId ? undefined : trackerSpend(data);
  const rows: TrackerWeekRow[] = weeks.map((week) => ({
    ...week,
    rollup: rollupWeek(data.config, touches, closeAs, week.weekStart, spend),
  }));
  const total = rollupRange(
    data.config,
    touches,
    closeAs,
    weeks[0].weekStart,
    addDays(weeks[weeks.length - 1].weekStart, 6),
    spend,
  );
  return { rows, total };
}

export type TrackerRollupColumn = {
  key: string;
  label: string;
  /** Money in cents; otherwise a count. */
  unit?: 'cents';
  /** Undefined where the figure doesn't apply: cost per lead with no leads, spend for one bet. */
  value(rollup: TrackerRollup): number | undefined;
};

/**
 * A course's rollup columns with its own labels: the four rollups on a
 * sequence course, Steps logged, Leads, Spend and Cost per lead on a Leads
 * course, or each stage of the first channel on a steps-only one, the rule
 * the coach List uses.
 */
export function trackerRollupColumns(config: TrackerConfig): TrackerRollupColumn[] {
  if (participantChannels(config)) {
    return [
      { key: 'steps', label: 'Steps logged', value: (r) => r.steps },
      { key: 'leads', label: 'Leads', value: (r) => r.leads },
      { key: 'spend', label: 'Spend', unit: 'cents', value: (r) => r.spendCents },
      {
        key: 'cpl',
        label: 'Cost per lead',
        unit: 'cents',
        value: (r) => cplOf(r.spendCents, r.leads),
      },
    ];
  }
  if (!config.channels.some((c) => c.flow === 'sequence')) {
    return (config.channels[0]?.stages ?? []).map((stage) => ({
      key: stage.key,
      label: stage.short || stage.label,
      value: (r) => r.byStage[stage.key] ?? 0,
    }));
  }
  const labels = config.rollupLabels;
  return [
    { key: 'sends', label: labels.sends, value: (r) => r.sends },
    { key: 'replies', label: labels.replies, value: (r) => r.replies },
    { key: 'held', label: labels.held, value: (r) => r.held },
    { key: 'closes', label: labels.closes, value: (r) => r.closes },
  ];
}

/** "Closes count when the money is collected, as Dana chose." */
export function closeChoiceNote(closeAs: TrackerCloseAs | undefined, firstName: string) {
  if (!closeAs) {
    return `${firstName} hasn't chosen how closes count.`;
  }
  return `Closes count when the money is ${closeAs}, as ${firstName} chose.`;
}

export type CoachPeopleGroup = {
  group: TrackerGroup;
  title: string;
  people: Array<{ person: TrackerPerson; next: TrackerNextText }>;
};

/** The participant's people in their Tracker tab's groups and order. */
export function coachPeopleGroups(data: TrackerSummaryData): CoachPeopleGroup[] {
  const { config, today, participant } = data;
  const byPerson = touchesByPerson(data.touches);
  const groups = new Map<TrackerGroup, TrackerPerson[]>();
  for (const person of data.people) {
    const group = trackerGroup(
      config,
      person,
      byPerson.get(person.id) ?? [],
      participant.closeAs,
      today,
    );
    groups.set(group, [...(groups.get(group) ?? []), person]);
  }
  return TRACKER_GROUP_ORDER.filter((g) => groups.get(g)?.length).map((group) => ({
    group,
    title: TRACKER_GROUP_TITLES[group],
    people: sortGroup(config, group, groups.get(group)!, byPerson).map((person) => ({
      person,
      next: nextText(config, person, byPerson.get(person.id) ?? [], today, participant.closeAs),
    })),
  }));
}

/** "Week 3 (16–22 Nov): Sends 8 · Replies 3 · Conversations held 1 · Closes 2 · $4,800 booked · $2,400 collected". */
export function weekRowText(data: CoachTrackerData, row: TrackerWeekRow) {
  const { config } = data;
  const closeAs = data.participant.closeAs;
  const items = [trackerCountsLine(config, row.rollup, { withCloses: true, closeAs })];
  if (config.channels.some((c) => c.flow === 'sequence')) {
    items.push(
      `${fmtMoney(row.rollup.bookedCents)} booked`,
      `${fmtMoney(row.rollup.collectedCents)} collected`,
    );
  }
  return `${weekLabel(row.weekNumber)} (${fmtWeekRange(row.weekStart)}): ${items.join(' · ')}`;
}

/** The tracker as text, appended to the standups a coach copies before a call. */
export function coachTrackerText(data: CoachTrackerData) {
  const { config } = data;
  const lines = ['TRACKER'];
  if (participantChannels(config)) {
    const setUp = leadsSetUpLine(data.participant);
    const channels = funnelsLine(config);
    lines.push(...[setUp, channels].filter((l): l is string => !!l));
  }
  const betsLine = betsListLine(data);
  if (betsLine) {
    lines.push(betsLine);
  }
  const closeAs = data.participant.closeAs;
  for (const row of participantWeekRows(data).rows) {
    lines.push(weekRowText(data, row));
    for (const line of betRollupLines(data, (touches) =>
      rollupWeek(config, touches, closeAs, row.weekStart),
    )) {
      lines.push(`  ${line}`);
    }
  }
  lines.push(`Scoreboard: ${scoreboardLine(data)}`);
  const byBetLine = betsScoreboardLine(data);
  if (byBetLine) {
    lines.push(`By bet: ${byBetLine}`);
  }
  for (const group of coachPeopleGroups(data)) {
    lines.push('', `${group.title} (${group.people.length})`);
    for (const { person, next } of group.people) {
      const parts = [
        personName(person),
        personOfferText(config, data.bets, person),
        stageLabel(config, person.stageKey),
        next.text,
      ];
      lines.push(`- ${parts.filter(Boolean).join(' · ')}`);
    }
  }
  return lines.join('\n');
}
